import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { tosAccelerate } from '../../../utils/tosAccelerate';

// ─── VoiceChip: 紧凑的音色标签组件 ──────────────────
export const VoiceChip: React.FC<{
  voiceUrl?: string;
  uploading: boolean;
  onUploadClick: () => void;
}> = ({ voiceUrl, uploading, onUploadClick }) => {
  const { t } = useTranslation('projectDetail');
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const togglePlay = () => {
    if (!voiceUrl) return;
    if (!audioRef.current) {
      const audio = new Audio(tosAccelerate(voiceUrl));
      audio.onended = () => setPlaying(false);
      audioRef.current = audio;
    }
    if (playing) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setPlaying(false);
    } else {
      audioRef.current.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
    }
  };

  // cleanup on unmount — setting src='' releases network resources and stops playback
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.onended = null;
        audioRef.current.src = '';
        audioRef.current = null;
      }
    };
  }, []);

  if (!voiceUrl) {
    return (
      <div style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
        <span style={{ fontSize: '0.75rem', color: 'var(--text-3)' }}>{t('character.replaceVoice')}</span>
        <button
          onClick={onUploadClick}
          disabled={uploading}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: '4px',
            background: 'var(--surface)', border: '1px dashed var(--border)', borderRadius: '14px',
            padding: '2px 10px 2px 8px', fontSize: '0.75rem', color: 'var(--text-3)',
            cursor: 'pointer', whiteSpace: 'nowrap', transition: 'all 0.15s',
          }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--primary)'; e.currentTarget.style.color = 'var(--primary)'; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--text-3)'; }}
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
            <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
            <line x1="12" y1="19" x2="12" y2="23" />
          </svg>
          {uploading ? t('uploading', { ns: 'common' }) : t('character.uploadVoice')}
        </button>
      </div>
    );
  }

  return (
    <div style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
      <span style={{ fontSize: '0.75rem', color: 'var(--text-3)' }}>{t('character.replaceVoice')}</span>
      <div style={{
        display: 'inline-flex', alignItems: 'center', gap: '2px',
        background: 'var(--success-light)', border: '1px solid var(--success)',
        borderRadius: '14px', padding: '3px 6px 3px 8px',
      }}>
        {/* 播放/停止 */}
        <button
          onClick={togglePlay}
          style={{
            background: 'none', border: 'none', cursor: 'pointer', padding: '1px',
            display: 'flex', alignItems: 'center', color: 'var(--success)',
          }}
          title={playing ? t('character.stopTitle') : t('character.listenTitle')}
        >
          {playing ? (
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
              <rect x="6" y="4" width="4" height="16" rx="1" />
              <rect x="14" y="4" width="4" height="16" rx="1" />
            </svg>
          ) : (
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
              <polygon points="5,3 19,12 5,21" />
            </svg>
          )}
        </button>
        <span style={{ fontSize: '0.75rem', color: 'var(--success)', fontWeight: 500 }}>{t('character.voiceSet')}</span>
        {/* 更换 */}
        <button
          onClick={onUploadClick}
          disabled={uploading}
          style={{
            background: 'none', border: 'none', cursor: 'pointer', padding: '1px 2px',
            display: 'flex', alignItems: 'center', color: 'var(--text-3)',
            transition: 'color 0.15s',
          }}
          onMouseEnter={e => { e.currentTarget.style.color = 'var(--primary)'; }}
          onMouseLeave={e => { e.currentTarget.style.color = 'var(--text-3)'; }}
          title={t('character.replaceVoiceTitle')}
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="17 8 12 3 7 8" />
            <line x1="12" y1="3" x2="12" y2="15" />
          </svg>
        </button>
      </div>
    </div>
  );
};
